import { isoToDay } from "./dayMath";

/**
 * The planner's data: resources, the projects, scopes and task groups they work in, and for each resource the
 * allocations it holds together with the tiles of time those allocations are complete for.
 *
 * Everything here is plain immutable data and pure functions. An update returns a new `data` that shares every
 * part it did not change with the old one, so a component that selects one resource's allocations is re-rendered
 * only when that resource changed.
 *
 * For each resource the data keeps this promise: it holds every allocation that overlaps one of its covered tiles
 * (and, when it came with a page, every allocation without dates). Allocations that only overlap other tiles may
 * be missing. The server's delta responses are built on the same promise.
 */

export function createPlannerData() {
  return {
    /** Order of the resources as the server paged them. */
    order: [],
    resources: {},
    projects: {},
    scopes: {},
    groups: {},
    groupsByScope: {},
    /** Bumped whenever a resource, project, scope or group changes, so the rows built from them can be reused. */
    structureVersion: 0,
    allocs: {},
  };
}

const EMPTY_TILES = new Set();

function createResourceAllocs() {
  return { version: 0, byTask: {}, dated: [], undated: [], tiles: EMPTY_TILES, inflight: EMPTY_TILES };
}

const byStart = (a, b) => a.startDay - b.startDay || a.endDay - b.endDay || (a.taskId < b.taskId ? -1 : a.taskId > b.taskId ? 1 : 0);

/** An allocation as the server sends it, turned into what the planner works with. Both dates absent means undated. */
export function normalizeAllocation(dto) {
  const startDay = dto.startDate && dto.endDate ? isoToDay(dto.startDate) : null;
  const endDay = startDay === null ? null : isoToDay(dto.endDate);
  const dated = startDay !== null && endDay >= startDay;

  return {
    key: dto.taskId,
    resourceId: dto.resourceId,
    taskId: dto.taskId,
    name: dto.name,
    refId: dto.refId,
    status: dto.status,
    priority: dto.priority ?? null,
    progress: dto.progress ?? 0,
    isCritical: Boolean(dto.isCritical),
    isMilestone: Boolean(dto.isMilestone),
    projectId: dto.projectId,
    scopeId: dto.scopeId,
    groupId: dto.taskGroupId ?? null,
    startDay: dated ? startDay : null,
    endDay: dated ? endDay : null,
    startDate: dated ? dto.startDate : null,
    endDate: dated ? dto.endDate : null,
    hoursPerDay: dto.hoursPerDay ?? 0,
    totalHours: dto.totalHours ?? 0,
    includeWeekends: Boolean(dto.isIncludeWeekends),
  };
}

export const isDated = (allocation) => allocation.startDay !== null;

function normalizeResource(dto) {
  return {
    id: dto.id,
    name: dto.name,
    image: dto.image,
    email: dto.email,
    title: dto.title,
    phoneNumber: dto.phoneNumber,
    isExternal: dto.isExternal,
    isActive: dto.isActive,
    maxWeeklyHours: dto.maxWeeklyHours ?? 40,
    memberships: (dto.projects || []).map((membership) => ({
      projectId: membership.projectId,
      maxWeeklyHours: membership.maxWeeklyHours ?? 0,
      scopeIds: membership.scopeIds || [],
    })),
  };
}

/** Same fields, so replacing an allocation by an identical one changes nothing. */
function sameAllocation(a, b) {
  return (
    a.startDay === b.startDay &&
    a.endDay === b.endDay &&
    a.hoursPerDay === b.hoursPerDay &&
    a.totalHours === b.totalHours &&
    a.includeWeekends === b.includeWeekends &&
    a.name === b.name &&
    a.refId === b.refId &&
    a.status === b.status &&
    a.priority === b.priority &&
    a.progress === b.progress &&
    a.isCritical === b.isCritical &&
    a.isMilestone === b.isMilestone &&
    a.projectId === b.projectId &&
    a.scopeId === b.scopeId &&
    a.groupId === b.groupId
  );
}

/** Rebuilds the ordered lists of a resource's allocations from the allocations by task. */
function withLists(allocs, byTask) {
  const all = Object.values(byTask);
  return {
    ...allocs,
    byTask,
    dated: all.filter(isDated).sort(byStart),
    undated: all.filter((allocation) => !isDated(allocation)).sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0)),
  };
}

/** Adds or replaces allocations by task. Returns the same object when nothing changed. */
function mergeAllocations(allocs, incoming) {
  let byTask = allocs.byTask;
  let changed = false;

  for (const allocation of incoming) {
    const existing = byTask[allocation.taskId];
    if (existing && sameAllocation(existing, allocation)) continue;

    if (!changed) byTask = { ...byTask };
    byTask[allocation.taskId] = allocation;
    changed = true;
  }

  return changed ? { ...withLists(allocs, byTask), version: allocs.version + 1 } : allocs;
}

function mergeNodes(nodes, incoming) {
  let next = nodes;
  for (const node of incoming) {
    if (next[node.id] && JSON.stringify(next[node.id]) === JSON.stringify(node)) continue;
    if (next === nodes) next = { ...nodes };
    next[node.id] = node;
  }
  return next;
}

function mergeGroups(data, groups) {
  const nextGroups = mergeNodes(data.groups, groups);
  if (nextGroups === data.groups) return { groups: data.groups, groupsByScope: data.groupsByScope };

  const groupsByScope = { ...data.groupsByScope };
  for (const group of groups) {
    const ids = groupsByScope[group.scopeId] || [];
    if (!ids.includes(group.id)) groupsByScope[group.scopeId] = [...ids, group.id];
  }
  return { groups: nextGroups, groupsByScope };
}

/** Applies the nodes (projects, scopes and groups) of a response. */
function mergeStructure(data, response) {
  const projects = mergeNodes(data.projects, response.projects || []);
  const scopes = mergeNodes(data.scopes, response.scopes || []);
  const { groups, groupsByScope } = mergeGroups(data, response.groups || []);

  if (projects === data.projects && scopes === data.scopes && groups === data.groups) return data;
  return { ...data, projects, scopes, groups, groupsByScope, structureVersion: data.structureVersion + 1 };
}

function allocationsByResource(allocations) {
  const byResource = {};
  for (const dto of allocations || []) {
    const allocation = normalizeAllocation(dto);
    (byResource[allocation.resourceId] ||= []).push(allocation);
  }
  return byResource;
}

const withTiles = (allocs, tiles, inflight) => ({ ...allocs, tiles, inflight });

function addTiles(existing, tiles) {
  const next = new Set(existing);
  for (const tile of tiles) next.add(tile);
  return next;
}

function removeTiles(existing, tiles) {
  if (existing.size === 0) return existing;
  const next = new Set(existing);
  for (const tile of tiles) next.delete(tile);
  return next;
}

/**
 * Applies a page of resources. A first page replaces everything; a later one is appended.
 * The allocations of the page are complete for `tiles`, so those tiles become covered for its resources.
 */
export function applyPage(data, response, { replace, tiles }) {
  const base = replace ? createPlannerData() : data;
  let next = mergeStructure({ ...base, structureVersion: base.structureVersion + 1 }, response);

  const resources = { ...next.resources };
  const allocs = { ...next.allocs };
  const order = replace ? [] : [...next.order];
  const incoming = allocationsByResource(response.allocations);

  for (const dto of response.resources || []) {
    const node = normalizeResource(dto);
    resources[node.id] = node;
    if (!order.includes(node.id)) order.push(node.id);

    const current = allocs[node.id] || createResourceAllocs();
    const merged = mergeAllocations(current, incoming[node.id] || []);
    allocs[node.id] = withTiles(merged, addTiles(merged.tiles, tiles), removeTiles(merged.inflight, tiles));
  }

  next = { ...next, resources, allocs, order };
  return next;
}

/** Applies the response to a delta request for some resources: allocations that overlap the requested windows. */
export function applyDelta(data, response, { resourceIds, tiles }) {
  const next = mergeStructure(data, response);
  const incoming = allocationsByResource(response.allocations);
  const allocs = { ...next.allocs };

  for (const id of resourceIds) {
    const current = allocs[id];
    if (!current) continue;

    const merged = mergeAllocations(current, incoming[id] || []);
    allocs[id] = withTiles(merged, addTiles(merged.tiles, tiles), removeTiles(merged.inflight, tiles));
  }

  return { ...next, allocs };
}

/**
 * Applies a fresh page for resources that are already listed and replaces what was held for them: allocations that
 * no longer exist disappear, and the covered tiles become exactly the ones the page was requested for.
 */
export function replaceResources(data, response, { tiles }) {
  let next = mergeStructure({ ...data, structureVersion: data.structureVersion + 1 }, response);

  const resources = { ...next.resources };
  const allocs = { ...next.allocs };
  const incoming = allocationsByResource(response.allocations);

  for (const dto of response.resources || []) {
    const node = normalizeResource(dto);
    if (!resources[node.id]) continue;

    resources[node.id] = node;
    const fresh = mergeAllocations(createResourceAllocs(), incoming[node.id] || []);
    const previous = allocs[node.id];
    allocs[node.id] = withTiles({ ...fresh, version: (previous ? previous.version : 0) + 1 }, new Set(tiles), EMPTY_TILES);
  }

  next = { ...next, resources, allocs };
  return next;
}

/** Marks tiles as being fetched for some resources. */
export function markInflight(data, resourceIds, tiles) {
  const allocs = { ...data.allocs };
  for (const id of resourceIds) {
    if (allocs[id]) allocs[id] = { ...allocs[id], inflight: addTiles(allocs[id].inflight, tiles) };
  }
  return { ...data, allocs };
}

/** Marks tiles as no longer being fetched, for when a request failed or was abandoned. */
export function clearInflight(data, resourceIds, tiles) {
  const allocs = { ...data.allocs };
  for (const id of resourceIds) {
    if (allocs[id]) allocs[id] = { ...allocs[id], inflight: removeTiles(allocs[id].inflight, tiles) };
  }
  return { ...data, allocs };
}

/** Changes some fields of one allocation, for an edit that is shown before the server has confirmed it. */
export function patchAllocation(data, resourceId, taskId, patch) {
  const current = data.allocs[resourceId];
  const allocation = current?.byTask[taskId];
  if (!allocation) return data;

  const next = { ...allocation, ...patch };
  if (patch.startDate && patch.endDate) {
    next.startDay = isoToDay(patch.startDate);
    next.endDay = isoToDay(patch.endDate);
  }

  if (sameAllocation(allocation, next)) return data;

  return { ...data, allocs: { ...data.allocs, [resourceId]: mergeAllocations(current, [next]) } };
}

export const getAllocation = (data, resourceId, taskId) => data.allocs[resourceId]?.byTask[taskId] ?? null;
